import { useState } from 'react';
import { useLanguage, type Locale } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { buildInterviewBrief } from '../prompts/interviewBrief';
import { X, Sparkles, Globe } from 'lucide-react';
import type { ProjectInput } from '../types';

export type IntakeKind = 'workspace' | 'organization' | 'project';

interface NewProjectIntakeProps {
  onClose: () => void;
  onStarted: (sessionId: string, kind: IntakeKind, name: string, input: ProjectInput) => void;
  initialName?: string;
  initialIdea?: string;
  initialDirection?: string;
  kind?: IntakeKind;
  prefillProjectId?: string;
}

const LABELS: Record<Locale, { project: string; organization: string; advance: string }> = {
  en: { project: 'New Project', organization: 'New Organization', advance: 'Advance Project' },
  de: { project: 'Neues Projekt', organization: 'Neue Organisation', advance: 'Projekt vertiefen' },
};

export default function NewProjectIntake({
  onClose,
  onStarted,
  initialName = '',
  initialIdea = '',
  initialDirection = '',
  kind = 'workspace',
  prefillProjectId,
}: NewProjectIntakeProps) {
  const { t, locale: appLocale } = useLanguage();
  const i = t.interview;
  const [name, setName] = useState(initialName);
  const [problem, setProblem] = useState('');
  const [projectIdea, setProjectIdea] = useState(initialIdea);
  const [locale, setLocale] = useState<Locale>(appLocale);
  const [direction, setDirection] = useState(initialDirection);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isWorkspace = kind === 'workspace';
  const titleKey: 'project' | 'organization' | 'advance' =
    kind === 'project' ? 'advance' : kind === 'workspace' ? 'project' : 'organization';
  const title = LABELS[locale][titleKey];
  const subtitle =
    kind === 'organization'
      ? i.organizationIntakeSubtitle
      : kind === 'project'
      ? i.advanceSubtitle
      : i.intakeSubtitle;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(i.intakeErrors.nameRequired);
      return;
    }
    // Combine problem + project idea into the 'idea' seed string the LLM expects.
    const problemTrim = problem.trim();
    const ideaTrim = isWorkspace ? projectIdea.trim() : initialIdea.trim();
    if (isWorkspace && !problemTrim && !ideaTrim) {
      setError(i.intakeErrors.atLeastOneRequired);
      return;
    }
    if (!isWorkspace && !ideaTrim) {
      setError(i.intakeErrors.ideaRequired);
      return;
    }
    const combinedIdea = isWorkspace
      ? [problemTrim && `Problem: ${problemTrim}`, ideaTrim && `Idea: ${ideaTrim}`]
          .filter(Boolean)
          .join('\n\n')
      : ideaTrim;
    setError('');
    setSubmitting(true);

    const seed = { name: name.trim(), idea: combinedIdea };
    const brief = buildInterviewBrief({
      kind,
      seed,
      locale,
      direction: direction.trim() || undefined,
    });

    try {
      const response = await fetch('/api/interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          op: 'start',
          payload: {
            kind,
            seed,
            projectId: prefillProjectId,
            locale,
            brief,
            direction: direction.trim() || undefined,
          },
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to start interview');
        setSubmitting(false);
        return;
      }
      if (data.session?.id) {
        onStarted(
          data.session.id,
          kind,
          name.trim(),
          {
            idea: combinedIdea,
            industry: undefined,
            location: undefined,
            teamSize: undefined,
            stage: undefined,
            goals: undefined,
            locale,
          }
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start interview');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-stretch sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white shadow-2xl w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-auto border-0 sm:border border-cloud flex flex-col">
        <div className="p-4 sm:p-6 border-b border-cloud flex items-center justify-between sticky top-0 bg-white z-10 safe-top">
          <div className="min-w-0 flex-1 pr-2">
            <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <Sparkles size={20} className="text-red-500 shrink-0" />
              <span className="truncate">{title}</span>
            </h2>
            <p className="text-slate text-sm mt-1">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-1 text-ash hover:text-ink hover:bg-mist transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 flex-1">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 text-sm border border-red-100">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink mb-1">{i.intakeName} *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={i.intakeNamePlaceholder}
              className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            />
          </div>

          {isWorkspace ? (
            <>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">{i.intakeProblem}</label>
                <p className="text-xs text-slate mb-2">{i.intakeProblemHelp}</p>
                <textarea
                  value={problem}
                  onChange={(e) => setProblem(e.target.value)}
                  placeholder={i.intakeProblemPlaceholder}
                  rows={3}
                  className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">{i.intakeProjectIdea}</label>
                <p className="text-xs text-slate mb-2">{i.intakeProjectIdeaHelp}</p>
                <textarea
                  value={projectIdea}
                  onChange={(e) => setProjectIdea(e.target.value)}
                  placeholder={i.intakeProjectIdeaPlaceholder}
                  rows={3}
                  className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm font-medium text-ink mb-1">{i.intakeIdea} *</label>
              <textarea
                value={projectIdea}
                onChange={(e) => setProjectIdea(e.target.value)}
                placeholder={i.intakeIdeaPlaceholder}
                rows={3}
                className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink mb-1">{i.directionLabel}</label>
            <p className="text-xs text-slate mb-2">{i.directionHelp}</p>
            <textarea
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder={i.directionPlaceholder}
              rows={2}
              className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div className="border border-cloud p-4 bg-paper">
            <div className="flex items-start gap-3">
              <Globe size={18} className="text-slate shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-ink mb-1">{i.intakeLanguage}</label>
                <p className="text-xs text-slate mb-3">{i.intakeLanguageHelp}</p>
                <div className="flex flex-wrap">
                  <LanguageSwitcher value={locale} onChange={setLocale} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-cloud safe-bottom">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] px-4 py-2 text-ink hover:bg-mist font-medium transition-colors"
            >
              {i.intakeCancel}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="min-h-[44px] px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-50"
            >
              {submitting ? '...' : i.intakeStart}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
